import * as React from "react"
import * as ReactDOM from "react-dom"
import { cn } from "@/utils/cn"

interface TooltipContextType {
    open: boolean
    setOpen: (open: boolean) => void
    triggerRect: DOMRect | null
    setTriggerRect: (rect: DOMRect | null) => void
}

const TooltipContext = React.createContext<TooltipContextType | null>(null)

const TooltipProvider = ({ children }: { children: React.ReactNode }) => {
    return <>{children}</>
}

const Tooltip = ({ children }: { children: React.ReactNode }) => {
    const [open, setOpen] = React.useState(false)
    const [triggerRect, setTriggerRect] = React.useState<DOMRect | null>(null)

    return (
        <TooltipContext.Provider value={{ open, setOpen, triggerRect, setTriggerRect }}>
            {children}
        </TooltipContext.Provider>
    )
}

const TooltipTrigger = React.forwardRef<
    HTMLElement,
    { children: React.ReactNode; asChild?: boolean } & React.HTMLAttributes<HTMLElement>
>(({ children, asChild, ...props }, ref) => {
    const context = React.useContext(TooltipContext)
    const internalRef = React.useRef<HTMLElement>(null)

    if (!context) throw new Error("TooltipTrigger must be used within Tooltip")

    const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        context.setTriggerRect(e.currentTarget.getBoundingClientRect())
        context.setOpen(true)
    }

    const handleMouseLeave = () => {
        context.setOpen(false)
    }

    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children as any, {
            ...props,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            ref: (children as any).ref || ref || internalRef
        })
    }

    return (
        <span
            ref={ref as any || internalRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="inline-block"
            {...props}
        >
            {children}
        </span>
    )
})
TooltipTrigger.displayName = "TooltipTrigger"

const TooltipContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { sideOffset?: number; side?: "top" | "bottom" | "left" | "right" | "auto" }
>(({ className, sideOffset = 8, side = "top", ...props }, ref) => {
    const context = React.useContext(TooltipContext)
    const [mounted, setMounted] = React.useState(false)

    React.useEffect(() => {
        setMounted(true)
    }, [])

    if (!context || !context.open || !context.triggerRect || !mounted) return null

    const { triggerRect } = context

    // Calculate available space on each side
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const spaceTop = triggerRect.top
    const spaceBottom = viewportHeight - triggerRect.bottom
    const spaceLeft = triggerRect.left
    const spaceRight = viewportWidth - triggerRect.right

    // Determine best side if auto is selected
    let effectiveSide: "top" | "bottom" | "left" | "right" = side as "top" | "bottom" | "left" | "right"
    if (side === "auto") {
        // Find the side with most space
        const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight)
        if (maxSpace === spaceTop) effectiveSide = "top"
        else if (maxSpace === spaceBottom) effectiveSide = "bottom"
        else if (maxSpace === spaceLeft) effectiveSide = "left"
        else effectiveSide = "right"
    }

    let top = 0
    let left = 0

    switch (effectiveSide) {
        case "top":
            top = triggerRect.top + window.scrollY - sideOffset
            left = triggerRect.left + window.scrollX + triggerRect.width / 2
            break
        case "bottom":
            top = triggerRect.bottom + window.scrollY + sideOffset
            left = triggerRect.left + window.scrollX + triggerRect.width / 2
            break
        case "left":
            top = triggerRect.top + window.scrollY + triggerRect.height / 2
            left = triggerRect.left + window.scrollX - sideOffset
            break
        case "right":
            top = triggerRect.top + window.scrollY + triggerRect.height / 2
            left = triggerRect.right + window.scrollX + sideOffset
            break
    }

    const content = (
        <div
            ref={ref}
            style={{
                top: `${top}px`,
                left: `${left}px`,
            }}
            className={cn(
                "absolute pointer-events-none z-[9999] max-w-xs",
                "animate-in fade-in-0 zoom-in-95 duration-200",
                effectiveSide === "top" && "-translate-x-1/2 -translate-y-full slide-in-from-bottom-2",
                effectiveSide === "bottom" && "-translate-x-1/2 slide-in-from-top-2",
                effectiveSide === "left" && "-translate-x-full -translate-y-1/2 slide-in-from-right-2",
                effectiveSide === "right" && "-translate-y-1/2 slide-in-from-left-2",
                "px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-lg",
                className
            )}
            {...props}
        />
    )

    return ReactDOM.createPortal(content, document.body)
})
TooltipContent.displayName = "TooltipContent"

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
